import { useEffect, useRef, type ReactNode } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * Ícones do ranking que fazem o gesto do próprio nome. Os desenhos são os do
 * Lucide (os mesmos ícones da plataforma), só que em camadas que se mexem:
 *
 *   FogoVivo        — queima: três línguas (vermelha, laranja, miolo amarelo)
 *                     tremulando em ritmos diferentes, presas pela base.
 *   CoroaCoroando   — desce de cima, assenta, amassa um tico e se acomoda,
 *                     como se pousasse numa cabeça.
 *   RaioCaindo      — o traço risca de cima a baixo e o raio pisca duas vezes,
 *                     como um relâmpago, antes de ficar aceso.
 *   EstrelaGirando  — uma volta inteira, passando um pouco e voltando.
 *
 * Só o fogo é contínuo (fogo parado não é fogo). Os outros tocam ao aparecer
 * e de novo quando o mouse entra no card (o ancestral com a classe `group`).
 */

/** Toca a animação ao montar e de novo a cada mouseenter no `.group` de cima. */
function useTocar<T extends Element>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tocar = () => {
      el.classList.remove('tocar');
      // força o navegador a ver a classe saindo antes de voltar
      void (el as unknown as HTMLElement).getBoundingClientRect();
      el.classList.add('tocar');
    };
    tocar();
    const card = el.closest('.group');
    card?.addEventListener('mouseenter', tocar);
    return () => card?.removeEventListener('mouseenter', tocar);
  }, []);
  return ref;
}

const CHAMA =
  'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z';

export function FogoVivo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn('fogo-vivo', className)} aria-hidden>
      <g className="fogo-lingua fogo-fora">
        <path d={CHAMA} fill="#ef4444" />
      </g>
      <g transform="translate(12 22) scale(.74) translate(-12 -22)">
        <g className="fogo-lingua fogo-meio">
          <path d={CHAMA} fill="#f97316" />
        </g>
      </g>
      <g transform="translate(12 22) scale(.46) translate(-12 -22)">
        <g className="fogo-lingua fogo-miolo">
          <path d={CHAMA} fill="#fde047" />
        </g>
      </g>
    </svg>
  );
}

const COROA =
  'M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z';

export function CoroaCoroando({
  className,
  base = true,
}: {
  className?: string;
  /** a linha de baixo do ícone (some quando a coroa pousa numa cabeça) */
  base?: boolean;
}) {
  const ref = useTocar<SVGSVGElement>();
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('coroa-coroando overflow-visible', className)}
      aria-hidden
    >
      <g className="coroa-corpo">
        <path d={COROA} fill="currentColor" fillOpacity={0.18} />
        {base && <path d="M5 21h14" />}
      </g>
    </svg>
  );
}

const RAIO =
  'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z';

export function RaioCaindo({ className }: { className?: string }) {
  const ref = useTocar<SVGSVGElement>();
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('raio-caindo overflow-visible', className)}
      aria-hidden
    >
      <path className="raio-luz" d={RAIO} fill="currentColor" />
      <path className="raio-traco" d={RAIO} pathLength={1} />
    </svg>
  );
}

export function EstrelaGirando({ className }: { className?: string }) {
  const ref = useTocar<HTMLSpanElement>();
  return (
    <span ref={ref} className="estrela-girando inline-flex">
      <Star className={className} />
    </span>
  );
}

/** Coroa pousando na cabeça de quem está em 1º (por cima do avatar). */
export function CoroaNaCabeca({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute -top-[30%] left-1/2 z-10 -translate-x-1/2">
        <CoroaCoroando
          base={false}
          className="h-5 w-5 text-yellow-400 sm:h-6 sm:w-6"
        />
      </div>
      {children}
    </div>
  );
}
